import { useMemo } from "react";
import { Activity, Truck, Fuel, HardHat, AlertTriangle, CheckCircle, MessageSquare, Hand, Clock, Zap, Wrench, Settings, ArrowRightLeft, ThumbsUp, ThumbsDown } from "lucide-react";

const EVENT_CONFIG = {
  SHIFT_START: { icon: Zap, color: "text-lime-500", bg: "bg-lime-50" },
  SHIFT_STOP: { icon: Clock, color: "text-slate-400", bg: "bg-slate-100" },
  PLANNER_APPROVAL: { icon: CheckCircle, color: "text-lime-500", bg: "bg-lime-50" },
  DRIVER_DELIVERY_COMPLETE: { icon: Fuel, color: "text-lime-600", bg: "bg-lime-50" },
  DRIVER_BREAK: { icon: Clock, color: "text-amber-500", bg: "bg-amber-50" },
  DRIVER_BREAKDOWN: { icon: AlertTriangle, color: "text-red-500", bg: "bg-red-50" },
  DRIVER_FEEDBACK: { icon: MessageSquare, color: "text-sky-400", bg: "bg-sky-500/10" },
  MACHINE_ALERT: { icon: Fuel, color: "text-amber-500", bg: "bg-amber-50" },
  MACHINE_STATUS_CHANGE: { icon: HardHat, color: "text-purple-400", bg: "bg-purple-500/10" },
  MACHINE_DEFECT: { icon: Wrench, color: "text-red-500", bg: "bg-red-500/10" },
  MACHINE_MAINTENANCE: { icon: Settings, color: "text-purple-400", bg: "bg-purple-500/10" },
  TRANSPORT_REQUEST: { icon: ArrowRightLeft, color: "text-blue-400", bg: "bg-blue-500/10" },
  TRANSPORT_CONFIRMED: { icon: Truck, color: "text-blue-400", bg: "bg-blue-500/10" },
  DELIVERY_SUCCESS: { icon: ThumbsUp, color: "text-lime-600", bg: "bg-lime-50", textColor: "text-lime-300" },
  DELIVERY_INCIDENT: { icon: ThumbsDown, color: "text-red-400", bg: "bg-red-500/10", textColor: "text-red-300" },
  SITE_UPDATE: { icon: Settings, color: "text-slate-400", bg: "bg-slate-100" },
  LIKE_ACTION: { icon: Hand, color: "text-amber-500", bg: "bg-amber-50" },
};

export default function LiveFeed({ events = [] }) {
  const recentEvents = useMemo(() => events.slice(0, 8), [events]);

  if (recentEvents.length === 0) return null;

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="live-feed">
      <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-3">
        <Activity className="w-3.5 h-3.5 text-lime-500" /> Live Feed
      </h3>
      <div className="flex gap-3 overflow-x-auto pb-1 no-scrollbar">
        {recentEvents.map((event, i) => {
          const config = EVENT_CONFIG[event.event_type] || EVENT_CONFIG.MACHINE_ALERT;
          const Icon = config.icon;
          return (
            <div key={event.id || i}
              className={`min-w-[220px] p-3 rounded-xl border border-slate-200 ${config.bg} shrink-0 animate-fadeInUp`}
              style={{ animationDelay: `${i * 0.05}s` }}>
              <div className="flex items-center gap-2 mb-1.5">
                <Icon className={`w-3.5 h-3.5 ${config.color}`} />
                <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{event.event_type?.replace(/_/g, " ")}</span>
              </div>
              <p className={`text-[11px] font-bold leading-snug line-clamp-2 ${config.textColor || "text-slate-400"}`}>{event.message}</p>
              {event.driver_name && (
                <span className="text-[9px] font-bold text-slate-400 block mt-1.5">{event.driver_name} / {new Date(event.timestamp).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
